import api from './api';
import { 
  PerformanceSummaryResponse, 
  PerformanceAreasResponse, 
  PerformanceChartsResponse, 
  PerformanceSalesResponse,
  SalesDetailSummaryResponse,
  SalesDetailPipelineResponse,
  SalesDetailTrendsResponse,
  SalesDetailAnalyticsResponse
} from '../types/dashboard';

interface PerformanceParams {
  periode: number;
  month?: number;
  property_area_id?: string;
}

interface PerformanceAreaParams extends PerformanceParams {
  sort_by?: string;
  sort_order?: string;
}

interface PerformanceSalesParams extends PerformanceParams {
  page?: number;
  limit?: number;
  search?: string;
  sort_by?: string;
  sort_order?: string;
}

/**
 * Fetches the summary statistics of the performance dashboard.
 */
export const getPerformanceSummary = async (params: PerformanceParams) => {
  // STRICT COMPLIANCE: No leading slash on relative Axios route
  const response = await api.get<PerformanceSummaryResponse>('dashboard/performance/summary', {
    params,
  });
  return response.data;
};

/**
 * Fetches the performance by area statistics of the performance dashboard with sorting.
 */
export const getPerformanceAreas = async (params: PerformanceAreaParams) => {
  // STRICT COMPLIANCE: No leading slash on relative Axios route
  const response = await api.get<PerformanceAreasResponse>('dashboard/performance/areas', {
    params,
  });
  return response.data;
};

/**
 * Fetches the performance charts (trends, funnel, sources) of the performance dashboard.
 */
export const getPerformanceCharts = async (params: PerformanceParams) => {
  // STRICT COMPLIANCE: No leading slash on relative Axios route
  const response = await api.get<PerformanceChartsResponse>('dashboard/performance/charts', {
    params,
  });
  return response.data;
};

/**
 * Fetches the performance by sales agent statistics of the performance dashboard with pagination and search.
 */
export const getPerformanceSales = async (params: PerformanceSalesParams) => {
  // STRICT COMPLIANCE: No leading slash on relative Axios route
  const response = await api.get<PerformanceSalesResponse>('dashboard/performance/sales', {
    params,
  });
  return response.data;
};

/**
 * Fetches the property areas the user has access to.
 */
export const getPropertyAreasForRole = async () => {
  // STRICT COMPLIANCE: No leading slash on relative Axios route
  const response = await api.get('property-areas/role');
  return response.data;
};

/**
 * Fetches the individual sales summary statistics (Endpoint 1).
 */
export const getSalesDetailSummary = async (id: string, params: PerformanceParams) => {
  // STRICT COMPLIANCE: No leading slash on relative Axios route
  const response = await api.get<SalesDetailSummaryResponse>(`dashboard/performance/sales-detail/${id}/summary`, {
    params,
  });
  return response.data;
};

/**
 * Fetches the individual sales pipeline & funnel statistics (Endpoint 2).
 */
export const getSalesDetailPipeline = async (id: string, params: PerformanceParams) => {
  // STRICT COMPLIANCE: No leading slash on relative Axios route
  const response = await api.get<SalesDetailPipelineResponse>(`dashboard/performance/sales-detail/${id}/pipeline`, {
    params,
  });
  return response.data;
};

/**
 * Fetches the individual sales trends MoM statistics (Endpoint 3).
 */
export const getSalesDetailTrends = async (id: string, params: Omit<PerformanceParams, 'month'>) => {
  // STRICT COMPLIANCE: No leading slash on relative Axios route
  const response = await api.get<SalesDetailTrendsResponse>(`dashboard/performance/sales-detail/${id}/trends`, {
    params,
  });
  return response.data;
};

/**
 * Fetches the individual sales analytics statistics (SLA, sources, achievements) (Endpoint 4).
 */
export const getSalesDetailAnalytics = async (id: string, params: PerformanceParams) => {
  // STRICT COMPLIANCE: No leading slash on relative Axios route
  const response = await api.get<SalesDetailAnalyticsResponse>(`dashboard/performance/sales-detail/${id}/analytics`, {
    params,
  });
  return response.data;
};

